/**
 * System prompt builder - simplified approach
 * Produces output matching build_2025_09 format
 * Based on ArtifactChatIOS patterns
 */

/**
 * Build a structured system prompt with clear hierarchy
 * @param {string} instructions - Base instructions
 * @param {Object} contextData - Context data including workspace and profile info
 * @param {Object} options - Configuration options
 * @returns {Promise<string>} The structured system prompt
 */
export async function buildStructuredSystemPrompt(
  instructions,
  contextData,
  options = {}
) {
  const config = {
    enableMemories: false, // Disabled for production release - memory system not ready
    ...options,
  };

  const sections = [];

  // 1. Hierarchy (XML format)
  sections.push(buildSystemHierarchy());

  // 2. Meta instructions
  sections.push(await buildMetaInstructions());

  // 3. Model Set Context (Memories)
  if (config.enableMemories) {
    const memoryContext = await buildModelSetContext(contextData);
    if (memoryContext) sections.push(memoryContext);
  }

  // 4. Tool guidelines (simplified) - pass contextData to only include enabled tools
  sections.push(buildToolGuidelines(contextData));

  // 5. User profile
  const profileContext = await buildUserProfile();
  if (profileContext) sections.push(profileContext);

  // 6. Assistant role
  if (instructions?.trim()) {
    sections.push(buildAssistantRole(instructions, contextData));
  }

  // 7. Project context
  const projectContext = await buildProjectContext(contextData);
  if (projectContext) sections.push(projectContext);

  return `<system_prompt>\n${sections.filter(Boolean).join("\n\n")}\n</system_prompt>`;
}

/**
 * Build a simple system prompt without memory integration (fallback)
 * @param {string} instructions - Base instructions
 * @returns {string} Simple system prompt
 */
export function buildSimpleSystemPrompt(instructions) {
  if (!instructions?.trim()) return "";

  return `**ASSISTANT INSTRUCTIONS:**

${instructions.trim()}

**CORE DIRECTIVES:**
- You are a helpful AI assistant
- Provide comprehensive, detailed responses
- Always maintain a professional and helpful tone
- Follow the specific instructions provided above`;
}

/**
 * Build system hierarchy section with XML format
 * @returns {string} System hierarchy XML
 */
function buildSystemHierarchy() {
  return `<!-- HIERARCHY: PLATFORM_SAFETY (0) > META (1) > MEMORIES (1.5) > USER (2) > ASSISTANT (3) > WRITING_STYLE (3.5) > PROJECT (4) -->

<system_hierarchy>
    <enforcement_levels>
        <level priority="0" type="IMMUTABLE">PLATFORM_SAFETY</level>
        <level priority="1" type="META">META_INSTRUCTIONS</level>
        <level priority="1.5" type="PERSISTENT">MODEL_SET_CONTEXT</level>
        <level priority="2" type="USER">USER_PROFILE</level>
        <level priority="3" type="ASSISTANT">ASSISTANT_ROLE</level>
        <level priority="3.5" type="COMMUNICATION">WRITING_STYLE</level>
        <level priority="4" type="CONTEXTUAL">PROJECT_CONTEXT</level>
    </enforcement_levels>
</system_hierarchy>`;
}

/**
 * Get user location data for context
 * @returns {Promise<Object|null>} Location data or null
 */
async function getUserLocationData() {
  try {
    const { getLocationData, getLocalTimeInfo } = await import(
      "@/lib/utils/locationUtils"
    );

    const locationData = await getLocationData({
      useCache: true,
      cacheTimeout: 3600000, // 1 hour cache
      enableGPS: false,
      enableIP: true,
      enableTimezone: true,
    });

    if (!locationData) return null;

    const timeInfo = getLocalTimeInfo(locationData.timezone);

    return {
      ...locationData,
      ...timeInfo,
    };
  } catch (error) {
    console.error("Error getting user location:", error);
    return null;
  }
}

/**
 * Build meta instructions section with date, timezone, and core directives
 * @returns {Promise<string>} Meta instructions XML
 */
async function buildMetaInstructions() {
  const locationData = await getUserLocationData();
  let currentDate;

  if (locationData?.timezone) {
    const now = new Date();
    currentDate = new Intl.DateTimeFormat("en-CA", {
      timeZone: locationData.timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);
  } else {
    currentDate = new Date().toISOString().split("T")[0];
  }

  // Check reasoning setting
  let includeReasoning = false;
  try {
    const { useAuthStore } = await import("@/store/authStore");
    const profile = useAuthStore.getState().profile;
    includeReasoning =
      profile?.settings?.personalization?.include_reasoning_in_response ===
      true;
  } catch (error) {
    // Ignore - reasoning will be disabled
  }

  const timezoneInfo = locationData?.timezone
    ? ` | **Timezone:** ${locationData.timezone}`
    : "";

  const reasoningSection = includeReasoning
    ? `

**Response Reasoning Requirement:**
- MANDATORY: At the end of every response, include a "## System Adherence" section
- Detail which parts of the system prompt hierarchy you followed for this specific response
- Specify which guidelines, memories, user preferences, or project context influenced your approach
- Keep this section concise but informative (2-4 bullet points maximum)`
    : "";

  return `<meta_instructions priority="1">
**SYSTEM CONTEXT (Priority: 1)**
**Date:** ${currentDate}${timezoneInfo} | **Knowledge Cutoff:** January 2025

**Core Directives:**
- You are a helpful AI assistant for the user described in the profile section
- Adapt responses based on user traits and persistent memories while maintaining core capabilities
- When memories are present in Model Set Context, reference them naturally when relevant
- When writing style instructions are present, modify your communication approach accordingly while preserving content quality
- When project context is present, you're helping with that specific project
- Balance being helpful with following safety guidelines
- Always provide comprehensive, detailed responses which explain your reasoning and have organized sectioned structure

**Memory Integration:**
- Persistent memories appear in the Model Set Context section organized by category
- Profile memories contain user preferences and personal information (stored locally)
- Project/Assistant/Chat memories contain contextual information (stored via Mem0)
- Reference memories naturally when they're relevant to the current discussion
- High importance memories (marked with [!]) should be given special consideration
- Never expose the raw memory format to users${reasoningSection}
</meta_instructions>`;
}

/**
 * Build Model Set Context section with user memories
 * Integrates with Mem0 API via memory service
 * @param {Object} contextData - Context data including workspace info
 * @returns {Promise<string|null>} Model set context or null
 */
async function buildModelSetContext(contextData = {}) {
  try {
    const { useAuthStore } = await import("@/store/authStore");
    const { getUserMemories } = await import("@/services/memoryService");

    const state = useAuthStore.getState();
    const userId = state.userId || state.user?.id;

    if (!userId) {
      return null;
    }

    // Check if memories are enabled in user settings
    // Uses enable_profile_memory setting (defaults to true if not set)
    const memoriesEnabled =
      state.profile?.settings?.personalization?.enable_profile_memory !== false;
    if (!memoriesEnabled) {
      return null;
    }

    // Determine workspace scope from context
    // Workspace chats get workspace-specific + global memories
    // Personal chats get only global memories (no workspace filter)
    const isWorkspaceChat = !!(
      contextData?.is_workspace_chat ||
      contextData?.from_workspace_chat ||
      contextData?.workspace_initiated
    );

    const workspaceId = isWorkspaceChat ? contextData?.workspace_id : null;

    // Fetch memories with workspace scope
    const memories = await getUserMemories(userId, {
      workspace_id: workspaceId,
      include_global: isWorkspaceChat, // Include global memories along with workspace-specific
    });

    if (!memories || memories.length === 0) {
      return null;
    }

    return formatMemoriesSection(memories);
  } catch (error) {
    console.error("[Memory] Model set context error:", error);
    return null;
  }
}

/**
 * Build simplified tool guidelines section
 * @param {Object} contextData - Context data with force flags
 * @returns {string} Tool guidelines XML
 */
function buildToolGuidelines(contextData = {}) {
  const sections = [];

  // Check if any tools are enabled
  const hasAnyToolEnabled =
    contextData?.forceWebSearchTool === true ||
    contextData?.forceCanvasCreate === true ||
    contextData?.forceImageGenTool === true ||
    contextData?.forceImageEditTool === true ||
    contextData?.forceVideoGenTool === true ||
    contextData?.forceCodeInterpreterTool === true ||
    contextData?.forceYoutubeAnalyzerTool === true;

  // Base header
  sections.push(`<tool_guidelines priority="0">
**TOOL USAGE (Priority: 0 - Platform Safety)**

**CRITICAL: Tool Selection Policy**
- ONLY use tools that have been explicitly enabled for this request
- Do NOT automatically invoke any tool based on query content or keywords
- If no tools are explicitly enabled, respond using only your knowledge${
    !hasAnyToolEnabled
      ? `

**NO TOOLS ENABLED**
- Web search: NOT AVAILABLE - Do not output any <search_web> or tool invocation syntax
- Canvas creation: NOT AVAILABLE - Do not output any <canvas_create> or tool invocation syntax
- Image generation: NOT AVAILABLE - Do not attempt to generate images
- Code execution: NOT AVAILABLE - Do not attempt to execute code
- Video generation: NOT AVAILABLE - Do not attempt to generate videos
- Image editing: NOT AVAILABLE - Do not attempt to edit images

**Tool Request Responses:**
When user explicitly requests a tool that's not enabled:
1. State the tool isn't enabled and can be selected from the UI (max 1-2 sentences total)
2. Do NOT provide workarounds, detailed descriptions for image prompts, or lengthy alternatives
3. Do NOT create multi-section responses explaining what the tool would have done
4. Simply acknowledge the limitation and ask if they want text-based help instead
5. Example: "Image generation isn't currently enabled. You can select it from the UI. Would you like a text description instead?"`
      : ""
  }`);

  // Only include web search guidelines if explicitly forced
  if (contextData?.forceWebSearchTool === true) {
    // Check if compatibility mode was used (search results already in context)
    if (contextData?.webSearchCompatibilityMode === true) {
      sections.push(`
**Web Search:** [ENABLED - COMPATIBILITY MODE]
- **Status:** Web search has been executed and results are included in your message context
- **Action Required:** Use the web search results provided in the message context to answer the user's query
- **Citations:** Always cite sources in-line with markdown link format [Source Name](URL) where Source Name is a readable identifier (domain name or source title) and URL is the direct link (i.e. [supabase](https://supabase.com/docs/guides/auth/social-login)) immediately after relevant statements. Do not wrap citations in parentheses - use them naturally inline.
- **Quality:** Write an accurate, detailed, and comprehensive answer using the provided search results
- **Response Format:** Use markdown with ## headers and unordered lists. If results are empty or unhelpful, answer from your knowledge
- **Note:** Do NOT attempt to use any web_search tool - the results are already in your context`);
    } else {
      sections.push(`
**Web Search:** [ENABLED]
- **Tool:** web_search
- **Status:** Explicitly enabled by user - USE THIS TOOL
- **Citations:** Always cite sources in-line with markdown link format [Source Name](URL) where Source Name is a readable identifier (domain name or source title) and URL is the direct link (i.e. [supabase](https://supabase.com/docs/guides/auth/social-login)) immediately after relevant statements. Do not wrap citations in parentheses - use them naturally inline.
- **Quality:** Write an accurate, detailed, and comprehensive answer to the Query using provided search results.
- **Response Format:** Use markdown with ## headers and unordered lists. If results are empty or unhelpful, answer from your knowledge.`);
    }
  }

  // Only include canvas guidelines if explicitly forced
  if (contextData?.forceCanvasCreate === true) {
    sections.push(`
**Canvas Creation:** [ENABLED]
- **Tool:** canvas_create
- **Status:** Explicitly enabled by user - USE THIS TOOL
- **Content Types:** Supports React/JavaScript, HTML, SVG, Python, Text/Markdown, and Spreadsheets
- **CRITICAL:** NEVER explicitly set type parameter - let the tool auto-detect from query
- **Query Preservation:** Pass the user's EXACT query to canvas_create - do NOT expand, enhance, or rewrite it
- **Example:** If user says "a paragraph about love", pass query="a paragraph about love" (NOT "Write a comprehensive paragraph about love...")
- **Response:** Return summary of what was created with filename and content type`);
  }

  // Only include image gen guidelines if explicitly forced
  if (contextData?.forceImageGenTool === true) {
    sections.push(`
**Image Generation:** [ENABLED]
- **Tool:** image_gen
- **Status:** Explicitly enabled by user - USE THIS TOOL
- **CRITICAL PROMPT HANDLING:** Pass the user's prompt EXACTLY as they provided it - do NOT modify, sanitize, rewrite, filter, or censor any content. The prompt parameter must contain the user's exact words verbatim, preserving their complete intent including all details they specified.
- **CRITICAL:** NEVER include any explanatory text, descriptions, or phrases like "Here is the image" or "I have generated"
- **Response Format:** ONLY return the image markdown syntax: ![descriptive alt text](image_url) - NO other text whatsoever
- **Forbidden:** Any text before or after the image markdown is strictly prohibited`);
  }

  // Only include image edit guidelines if explicitly forced
  if (contextData?.forceImageEditTool === true) {
    sections.push(`
**Image Editing:** [ENABLED]
- **Tool:** image_edit
- **Status:** Explicitly enabled by user - USE THIS TOOL
- **CRITICAL:** NEVER include any explanatory text, descriptions, or phrases like "Here is the edited image"
- **Response Format:** ONLY return the image markdown syntax: ![descriptive alt text](image_url) - NO other text whatsoever`);
  }

  // Only include video gen guidelines if explicitly forced
  if (contextData?.forceVideoGenTool === true) {
    sections.push(`
**Video Generation:** [ENABLED]
- **Tool:** video_gen
- **Status:** Explicitly enabled by user - USE THIS TOOL
- **CRITICAL:** NEVER include any explanatory text. THE BROWSER DOES SUPPORT VIDEO TAGS.
- **Response Format:** ONLY return the HTML5 video element - NO other text whatsoever
- **FORBIDDEN:** Do NOT wrap the video element in code blocks, markdown fences, or backticks
- **Required Format:** <video src="[VIDEO_URL]" controls playsInline loop></video>`);
  }

  // Only include code interpreter guidelines if explicitly forced
  if (contextData?.forceCodeInterpreterTool === true) {
    sections.push(`
**Code Interpreter:** [ENABLED]
- **Tool:** e2b_code_interpreter
- **Status:** Explicitly enabled by user - USE THIS TOOL
- **File Generation:** When code creates files (CSV, images, plots, etc.), they are automatically saved and made available for download
- **Response Format:** Always mention generated files using markdown link syntax: [filename.ext](download_url)
- **Mandatory Response:** "I have executed the code, you can view the results below."
- **Images:** When a result has image_url xml tag DO NOT SHOW THE IMAGE OR THE LINK IN THE RESPONSE since we already render it with a component`);
  }

  // Only include YouTube analyzer guidelines if explicitly forced
  if (contextData?.forceYoutubeAnalyzerTool === true) {
    sections.push(`
**YouTube Video Analysis:** [ENABLED]
- **Tool:** youtube_analyzer
- **Status:** Explicitly enabled by user - USE THIS TOOL
- **Activate when:** User provides YouTube URLs or asks to analyze YouTube videos
- **Content:** Extracts comprehensive video metadata, transcripts with timestamps, and top comments
- **Citations:** Reference the analyzed video with proper attribution: [Video Title by Channel Name](YouTube_URL)`);
  }

  // Always include memory tools (these are passive/background)
  sections.push(`
**Memory Tools:** [ALWAYS AVAILABLE]
- **bio_memory:** Store important user information when shared
- **search_memory:** Recall specific information about the user
- **chat_memory:** Save important context from conversations
- **Note:** Profile memories are stored locally in user settings, others use Mem0 API`);

  sections.push(`</tool_guidelines>`);

  return sections.join("\n");
}

/**
 * Build user profile section with simplified format
 * @returns {Promise<string|null>} User profile XML or null
 */
async function buildUserProfile() {
  try {
    const { useAuthStore } = await import("@/store/authStore");
    const profile = useAuthStore.getState().profile;

    const profileContextEnabled =
      profile?.settings?.personalization?.enable_profile_context === true;
    if (!profileContextEnabled) return null;

    const customInstructions =
      profile?.settings?.personalization?.profile_context ||
      profile?.settings?.profile?.profile_context;
    const traits = profile?.settings?.personalization?.traits;
    const interests = profile?.settings?.personalization?.interests;
    const preferredName = profile?.settings?.profile?.display_name;

    const locationData = await getUserLocationData();

    if (
      !customInstructions?.trim() &&
      (!traits || traits.length === 0) &&
      (!interests || interests.length === 0) &&
      !preferredName &&
      !locationData
    ) {
      return null;
    }

    let content = `<user_profile priority="2">
**USER PROFILE (Priority: 2)**

`;

    // Identity & Location (simplified inline format)
    if (preferredName || locationData) {
      content += `**Identity:** ${preferredName || "User"}`;
      if (locationData) {
        const location = [
          locationData.city,
          locationData.region,
          locationData.country,
        ]
          .filter(Boolean)
          .join(", ");
        if (location) content += ` | **Location:** ${location}`;
        if (locationData.timezone) content += ` (${locationData.timezone})`;
        if (locationData.localTime)
          content += ` | **Local Time:** ${locationData.localTime}`;
      }
      content += "\n";
    }

    // Personality traits (override capability)
    if (traits && traits.length > 0) {
      content += `**Personality Traits:** ${traits.join(", ")} *(overrides conflicting instructions at lower levels)*\n`;
    }

    // Custom instructions
    if (customInstructions?.trim()) {
      content += `**Custom Instructions:** ${customInstructions.trim()}\n`;
    }

    // Interests
    if (interests && interests.length > 0) {
      content += `**Interests:** ${interests.join(", ")}\n`;
    }

    content += `
**Adaptation:** Naturally incorporate user context and memories. Respect preferences while maintaining core capabilities. Reference memories when relevant to current discussion.
</user_profile>`;

    return content;
  } catch (error) {
    console.error("Profile context error:", error);
    return null;
  }
}

/**
 * Build assistant role section
 * @param {string} instructions - Assistant instructions
 * @param {Object} contextData - Context data
 * @returns {string} Assistant role XML
 */
function buildAssistantRole(instructions, contextData) {
  const isWorkspaceChat =
    contextData?.is_workspace_chat ||
    contextData?.from_workspace_chat ||
    contextData?.workspace_initiated;

  const contextType = isWorkspaceChat
    ? "PROJECT_ASSISTANT"
    : "GENERAL_ASSISTANT";

  return `<assistant_role priority="3">
**ASSISTANT ROLE (Priority: 3)**
**Context:** ${contextType}

${instructions}

*Note: Work within framework established by higher-priority levels. User personality traits and persistent memories may modify behavioral aspects.*
</assistant_role>`;
}

/**
 * Build project context section
 * @param {Object} contextData - Context data
 * @returns {Promise<string|null>} Project context XML or null
 */
async function buildProjectContext(contextData) {
  const isWorkspaceChat =
    contextData?.is_workspace_chat ||
    contextData?.from_workspace_chat ||
    contextData?.workspace_initiated;

  if (!isWorkspaceChat) return null;

  try {
    const { useAuthStore } = await import("@/store/authStore");
    const workspaceData = useAuthStore.getState().getWorkspaceContextData();

    if (!workspaceData?.include_workspace_instructions) return null;

    const { name, description, instructions } = workspaceData;
    if (!name && !description && !instructions) return null;

    let content = `<project_context priority="4">
**PROJECT CONTEXT (Priority: 4)**
`;
    if (name) content += `**Name:** ${name}\n`;
    if (description) content += `**Description:** ${description}\n`;
    if (instructions) content += `**Instructions:** ${instructions}\n`;
    content += `*Project guidelines complement but don't override user preferences, memories, or assistant role.*
</project_context>`;

    return content;
  } catch (error) {
    console.error("Project context error:", error);
    return null;
  }
}

/**
 * Format memories into Model Set Context section
 * @param {Array} memories - Array of memory objects
 * @returns {string} Formatted memory section
 */
function formatMemoriesSection(memories) {
  // Helper to safely parse date, returns 0 for invalid dates (sorts to end)
  const getTimestamp = (m) => {
    const dateValue = m.created_at || m.updated_at;
    if (!dateValue) return 0;
    const parsed = new Date(dateValue);
    return isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  };

  // Sort by date (newest first)
  const sorted = [...memories].sort(
    (a, b) => getTimestamp(b) - getTimestamp(a)
  );

  // Group by category
  const byCategory = sorted.reduce((acc, m) => {
    const cat = m.category || "general";
    (acc[cat] = acc[cat] || []).push(m);
    return acc;
  }, {});

  const categoryLabels = {
    profile: "Profile & Preferences",
    project: "Project Context",
    assistant: "Assistant Behavior",
    chat: "Conversation History",
    preference: "User Preferences",
    constraint: "Constraints",
    goal: "Goals",
    general: "General Information",
  };

  // Get all categories that have memories (don't just check fixed list)
  const allCategories = Object.keys(byCategory);
  // Sort to prioritize known categories first, then alphabetical for unknown
  const knownOrder = ["profile", "preference", "constraint", "goal", "project", "assistant", "chat", "general"];
  const sortedCategories = allCategories.sort((a, b) => {
    const aIndex = knownOrder.indexOf(a);
    const bIndex = knownOrder.indexOf(b);
    if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });

  let content = `<model_set_context priority="1.5">
# Model Set Context
`;
  let index = 1;

  for (const cat of sortedCategories) {
    if (!byCategory[cat]?.length) continue;

    // Use known label or format the category name nicely
    const label = categoryLabels[cat] || cat.charAt(0).toUpperCase() + cat.slice(1).replace(/_/g, " ");
    content += `\n## ${label}\n`;
    for (const m of byCategory[cat]) {
      // Handle missing dates gracefully - fallback to current date if both are undefined
      const dateValue = m.created_at || m.updated_at;
      let date;
      if (dateValue) {
        const parsedDate = new Date(dateValue);
        date = !isNaN(parsedDate.getTime())
          ? parsedDate.toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0];
      } else {
        date = new Date().toISOString().split("T")[0];
      }
      const importance = m.metadata?.importance === "high" ? " [!]" : "";
      const source =
        m.metadata?.source === "profile_settings" ? " [Profile]" : "";
      const text = m.memory || m.content || "";
      content += `${index++}. [${date}]${importance}${source} ${text}\n`;
    }
  }

  content += `
**Note:** These are persistent memories saved across conversations. Profile memories are stored locally, while project/assistant/chat memories use Mem0's intelligent memory layer. High importance memories are marked with [!]. Incorporate this context naturally when relevant.
</model_set_context>`;

  return content;
}

// Export for testing
export { formatMemoriesSection, buildModelSetContext };
